
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, DollarSign, Clock, MessageSquare, XCircle, Ban, FileText } from 'lucide-react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { useAppContext } from '../../App.jsx';
import { CHATS_COLLECTION } from '../../config/constants';
import ServiceDetailsModal from '../servi/ServiceDetailsModal';

const ClientServiceCard = ({ service, onCancel }) => {
    const { db } = useAppContext();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [invoiceData, setInvoiceData] = useState(null);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const navigate = useNavigate();

    // LÓGICA DE PRIORIDADE: Mostra o nome da empresa se existir, senão o nome da pessoa
    const serviDetails = service.participantDetails?.[service.serviId];
    const serviName = serviDetails?.companyName || serviDetails?.name || 'Prestador';

    let formattedDateTime = 'Agendamento pendente';
    if (service.scheduledAt) {
        const date = service.scheduledAt.toDate ? service.scheduledAt.toDate() : new Date(service.scheduledAt);
        if (!isNaN(date)) {
            formattedDateTime = `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
        }
    }

    const statusConfig = {
        'Novo': { label: 'Solicitado', color: 'border-blue-500' },
        'Orçamento Enviado': { label: 'Orçamento Recebido', color: 'border-yellow-500' },
        'Orçamento Aceito': { label: 'Orçamento Aceito', color: 'border-green-500' },
        'Orçamento Recusado': { label: 'Orçamento Recusado', color: 'border-red-500' },
        'Agendado': { label: 'Agendado', color: 'border-indigo-500' },
        'Em Andamento': { label: 'Em Andamento', color: 'border-purple-500' },
        'Finalizado': { label: 'Finalizado', color: 'border-gray-400' },
        'Pago': { label: 'Pago', color: 'border-green-600' },
        'Cancelado': { label: 'Cancelado', color: 'border-gray-400' },
    };
    const currentStatus = statusConfig[service.status] || { label: service.status, color: 'border-gray-500' };

    const isHistory = ['Pago', 'Finalizado', 'Cancelado'].includes(service.status);
    const hasAcceptedQuote = service.quote?.status === 'accepted' || service.status === 'Pago' || service.status === 'Finalizado';

    const handleCancelClick = (e) => {
        e.stopPropagation();
        onCancel(service);
    };

    const handleGoToChat = (e) => {
        e.stopPropagation();
        navigate(`/chat/${service.id}`);
    };

    const handleDetailsClick = async (e) => {
        e.stopPropagation();
        if (invoiceData) {
            setIsDetailsModalOpen(true);
            return;
        }

        setIsLoadingDetails(true);
        try {
            const messagesRef = collection(db, CHATS_COLLECTION, service.id, 'messages');
            const q = query(messagesRef, where("type", "==", "invoice"), limit(1));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                const invoiceDoc = querySnapshot.docs[0].data().invoice;
                setInvoiceData(invoiceDoc);
                setIsDetailsModalOpen(true);
            } else {
                navigate(`/chat/${service.id}`);
            }
        } catch (error) {
            console.error("Erro ao buscar detalhes do serviço:", error);
        } finally {
            setIsLoadingDetails(false);
        }
    };

    return (
        <>
            <div onClick={() => setIsExpanded(!isExpanded)} className={`bg-white p-4 rounded-xl shadow-lg border-l-4 ${currentStatus.color} space-y-2 cursor-pointer hover:shadow-xl transition-shadow`}>
                <div className="flex justify-between items-start">
                    <div>
                        <div className="flex items-center"><User className="w-4 h-4 mr-2 text-gray-500" /><p className="font-bold text-gray-800">{serviName}</p></div>
                        <p className="text-gray-700 font-semibold ml-6">{service.serviceTitle}</p>
                    </div>
                    <span className="text-xs font-bold text-white px-2 py-1 rounded-full bg-gray-700 whitespace-nowrap">{currentStatus.label}</span>
                </div>

                <div className="mt-2 pt-2 border-t flex justify-between items-center text-sm">
                    {(service.finalValue || service.quote?.total) && (
                        <div className="flex items-center font-bold text-green-600">
                            <DollarSign className="w-4 h-4 mr-1" />
                            <span>{(service.finalValue || service.quote.total).toFixed(2).replace('.', ',')}</span>
                        </div>
                    )}
                    <div className="flex items-center font-semibold text-gray-600 ml-auto"><Clock className="w-4 h-4 mr-1.5" /><span>{formattedDateTime}</span></div>
                </div>

                {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-gray-200 animate-slide-up space-y-2">
                        <p className='text-xs text-gray-500 font-semibold'>AÇÕES:</p>
                        <div className="flex items-center space-x-2">
                            <button onClick={handleGoToChat} className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition">
                                <MessageSquare className="w-4 h-4 mr-2" />
                                <span className="text-sm font-semibold">Conversa</span>
                            </button>

                            {isHistory ? (
                                <button
                                    onClick={handleDetailsClick}
                                    disabled={isLoadingDetails}
                                    className="flex-1 flex items-center justify-center px-3 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
                                >
                                    <FileText className="w-4 h-4 mr-2" />
                                    <span className="text-sm font-semibold">
                                        {isLoadingDetails ? 'Buscando...' : 'Detalhes do Serviço'}
                                    </span>
                                </button>
                            ) : (
                                <button onClick={handleCancelClick} disabled={hasAcceptedQuote} className={`flex-1 flex items-center justify-center px-3 py-2 rounded-lg transition ${hasAcceptedQuote ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-red-500 text-white hover:bg-red-600'}`}>
                                    {hasAcceptedQuote ? <Ban className="w-4 h-4 mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                                    <span className="text-sm font-semibold">Cancelar</span>
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <ServiceDetailsModal
                isOpen={isDetailsModalOpen}
                onClose={() => setIsDetailsModalOpen(false)}
                invoice={invoiceData}
                chatInfo={service}
            />
        </>
    );
};

export default ClientServiceCard;
